import { PacingBarInfo } from "@blisspointmedia/bpm-types/src/Pacing";
import { formatNumber } from "../utils/format-utils";
import { PacingDataEntry } from "@blisspointmedia/bpm-types/dist/Pacing";

export const sortData = (pacingData: any[], sortOption: string, currentSegment: string): any[] => {
  let sortedData: any;
  const getComparable = (item: any) => {
    const value = item[currentSegment];
    return Array.isArray(value) ? value.join(" ") : value;
  };
  switch (sortOption) {
    case "Abc":
      return pacingData.sort((a, b) => {
        const aComparable = String(getComparable(a));
        const bComparable = String(getComparable(b));
        return aComparable.localeCompare(bComparable);
      });
    case "goalValue":
      sortedData = pacingData.sort((a, b) => b.goalValue - a.goalValue);
      return sortedData;
    default:
      return pacingData;
  }
};

export const pacingSortByOptions: {
  value: string;
  label: string;
}[] = [
  { value: "Abc", label: "Abc" },
  { value: "goalValue", label: "Goal Value" },
];

export const calculateCumulativeData = (data: any[]): any[] => {
  let cumulativeClearedSimpleSpend = 0;
  let cumulativeProjectedSimpleSpend = 0;
  let cumulativeClearedSimpleIdealSpend = 0;
  let cumulativeProjectedAdjustedSpend = 0;

  const cumulativeData: any[] = [];

  data.forEach(entry => {
    cumulativeClearedSimpleSpend += entry.simpleCleared;
    cumulativeProjectedSimpleSpend += entry.simpleProjected + entry.simpleCleared;
    cumulativeClearedSimpleIdealSpend += entry.simpleIdealCleared;
    cumulativeProjectedAdjustedSpend += entry.adjustedProjected + entry.simpleIdealCleared;

    cumulativeData.push({
      date: entry.date,
      cumulativeClearedSimpleSpend,
      cumulativeProjectedSimpleSpend,
      cumulativeClearedSimpleIdealSpend,
      cumulativeProjectedAdjustedSpend,
      goalValue: entry.goalValue,
    });
  });

  return cumulativeData;
};

export const isTotalBar = (
  total: boolean,
  marginLeft: number,
  numberOfSegments: string[]
): boolean => {
  if (total) {
    return true;
  }
  if (marginLeft === 1 && numberOfSegments.length > 1) {
    return true;
  }
  return false;
};

interface PositionOfBudgetLine {
  positionOfBudgetLine: string;
  xValue: number;
}

export const calculatePositionOfBudgetLine = (
  pacingBarData: PacingBarInfo
): PositionOfBudgetLine => {
  const { totalCleared, totalProjected, budget } = pacingBarData;
  let xValue = 1;
  let positionOfBudgetLine = "totalCleared";
  if (budget < totalCleared) {
    positionOfBudgetLine = "totalRemainder";
    xValue = budget / totalCleared;
  } else if (totalCleared < budget && Math.abs(totalCleared - budget) < totalProjected) {
    positionOfBudgetLine = "totalProjected";
    xValue = (budget - totalCleared) / totalProjected;
  }
  return { positionOfBudgetLine, xValue };
};

export const formatPacingBarValue = (
  pacingValue: number,
  valueTag: string,
  goalValue: number,
  isModal: boolean,
  isActual: boolean,
  goalName: string
): string => {
  const goalNameMetrics = ["ROAS", "CPX", "CPC", "CPM", "CTR"];
  if (goalNameMetrics.includes(goalName) && valueTag === "") {
    return "";
  }

  let formattedBarValue = "";
  if (isModal) {
    return `$${(pacingValue / 1000).toLocaleString("en-us", {
      maximumFractionDigits: 2,
    })}k/${((pacingValue / goalValue) * 100).toFixed(0)}% ${valueTag}`;
  }

  if (!isActual) {
    return `${((pacingValue / goalValue) * 100).toFixed(0)}% ${valueTag}`;
  }

  if (goalNameMetrics.includes(goalName)) {
    return `$${pacingValue.toLocaleString("en-us", {
      maximumFractionDigits: 2,
    })} ${valueTag}`;
  }

  if (pacingValue < 1000) {
    formattedBarValue = `$${pacingValue.toLocaleString("en-us", {
      maximumFractionDigits: 0,
    })} ${valueTag}`;
  } else {
    formattedBarValue = `$${(pacingValue / 1000).toLocaleString("en-us", {
      maximumFractionDigits: 2,
    })}k ${valueTag}`;
  }

  return formattedBarValue;
};

export const scalePacingBarWidth = (
  currentBudget: number,
  maxBudget: number,
  marginLeft: number
): number => {
  let width = 100 - marginLeft * 25;
  let sizingChange = maxBudget ? (currentBudget / maxBudget) * 15 : 15;
  width = width + sizingChange;
  return width;
};

const createGroupingKey = (entry: PacingDataEntry, keys: string[]): string => {
  return keys
    .map(key => {
      const value = entry[key];
      return Array.isArray(value) ? value.join(",") : value ?? "null"; // Handle arrays and nulls
    })
    .join("|");
};

const getNewestUniqueById = (data: PacingDataEntry[]): PacingDataEntry[] => {
  const sorted = [...data].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const seenIds = new Set<string>();
  const unique: PacingDataEntry[] = [];

  for (const item of sorted) {
    if (!seenIds.has(item.id)) {
      seenIds.add(item.id);
      unique.push(item);
    }
  }

  return unique;
};

export const groupBudgetsEntriesBySegment = (
  pacingData: PacingDataEntry[],
  keys: string[],
  goalName: string
): PacingDataEntry[] => {
  const allowedGoals = new Set(["ROAS", "CPX", "CPC", "CPM", "CTR"]);

  const uniqueData = getNewestUniqueById(pacingData);
  const groupedMap = new Map<string, PacingDataEntry>();
  const metaMap = new Map<string, { seenIds: Set<string>; sumGoal: number }>();

  const updatedPacingData = allowedGoals.has(goalName) ? uniqueData : pacingData;

  updatedPacingData.forEach(rawEntry => {
    const entry = { ...rawEntry } as PacingDataEntry;
    Object.keys(entry).forEach(field => {
      const v = entry[field];
      if (typeof v === "string" && !isNaN(Number(v))) {
        entry[field] = Number(v) as any;
      }
    });

    const groupKey = createGroupingKey(entry, keys);

    if (!groupedMap.has(groupKey)) {
      groupedMap.set(groupKey, { ...entry });
      metaMap.set(groupKey, { seenIds: new Set(), sumGoal: 0 });
    } else {
      const agg = groupedMap.get(groupKey)!;
      Object.keys(entry).forEach(field => {
        const newVal = entry[field];
        const oldVal = agg[field];
        if (typeof newVal === "number" && typeof oldVal === "number") {
          agg[field] = (oldVal as number) + newVal;
        }
      });
    }

    if (allowedGoals.has(entry.goalName)) {
      const meta = metaMap.get(groupKey)!;
      if (!meta.seenIds.has(entry.id)) {
        meta.seenIds.add(entry.id);
        meta.sumGoal += entry.goalValue;
      }
    }
  });

  const groupedBudgets = Array.from(groupedMap.entries()).map(([groupKey, agg]) => {
    const meta = metaMap.get(groupKey)!;
    if (meta.seenIds.size > 0) {
      const avg = meta.sumGoal / meta.seenIds.size;
      agg.simpleBooked = avg;
      agg.adjustedBooked = avg;
    }
    return agg;
  });
  const groupedBudgetsUpdated = generateGroupedDataForRevenueMetrics(groupedBudgets, goalName);
  return groupedBudgetsUpdated;
};

export const toFlatArray = (value: unknown): (string | number)[] => {
  if (Array.isArray(value)) {
    return value.flat();
  }
  if (typeof value === "string" || typeof value === "number") {
    return [value];
  }
  return [];
};

export const formatCellValue = (value: number | string): string => {
  if (typeof value === "number") {
    return Number.isInteger(value) ? formatNumber(value) : formatNumber(value, 2);
  }

  if (typeof value === "string") {
    return value.length === 0 ? "N/A" : value;
  }

  return formatNumber(value);
};

export const simpleDimensionDataFormatter = ({
  data,
  tableHeaders,
}: {
  data: any[];
  tableHeaders: { id: string; dimensionTypeName?: string }[];
}): Record<string, { label: any; value: any }>[] => {
  return data.map(row => {
    const result: Record<string, { label: any; value: any }> = {};
    tableHeaders.forEach(header => {
      if (header.dimensionTypeName) {
        const cellValue = row[header.id] ?? "";
        result[header.dimensionTypeName] = { label: formatCellValue(cellValue), value: cellValue };
      }
    });
    return result;
  });
};

const generateGroupedDataForRevenueMetrics = (
  groupedData: PacingDataEntry[],
  goal_name: string
): PacingDataEntry[] => {
  let updatedData: PacingDataEntry[] = [];
  switch (goal_name) {
    case "Budget":
      updatedData = groupedData;
      break;
    case "Revenue":
      updatedData = groupedData;
      break;
    case "CPX":
      updatedData = groupedData.map(item => {
        const { spend, kpiVolume, simpleBooked, totalKpiVolume, totalSpend } = item;
        const safeCpx = kpiVolume !== 0 ? spend / Number(kpiVolume) : 0;
        const safeTotalCpx = totalKpiVolume !== 0 ? Number(totalSpend) / Number(totalKpiVolume) : 0;

        return {
          ...item,
          simpleCleared: safeTotalCpx,
          simpleProjected: simpleBooked - safeCpx,
        };
      });
      break;
    case "ROAS":
      updatedData = groupedData.map(item => {
        const { spend, revenue, simpleBooked, totalRevenue, totalSpend } = item;
        const safeRoas = spend !== 0 ? revenue / spend : 0;
        const safeTotalRoas = totalSpend !== 0 ? Number(totalRevenue) / Number(totalSpend) : 0;

        return {
          ...item,
          simpleCleared: safeTotalRoas,
          simpleProjected: simpleBooked - safeRoas,
        };
      });
      break;
    case "CPC":
      updatedData = groupedData.map(item => {
        const { spend, clicks, simpleBooked, totalSpend, totalClicks } = item;
        const safeCpc = clicks !== 0 ? spend / Number(clicks) : 0;
        const totalSafeCpc = totalClicks !== 0 ? Number(totalSpend) / Number(totalClicks) : 0;

        return {
          ...item,
          simpleCleared: totalSafeCpc,
          simpleProjected: simpleBooked - safeCpc,
        };
      });
      break;
    case "CPM":
      updatedData = groupedData.map(item => {
        const { spend, impressions, simpleBooked, totalImpressions, totalSpend } = item;
        const safeCpm = impressions !== 0 ? (spend * 1000) / Number(impressions) : 0;
        const safeTotalCpm =
          totalImpressions !== 0 ? (Number(totalSpend) * 1000) / Number(totalImpressions) : 0;

        return {
          ...item,
          simpleCleared: safeTotalCpm,
          simpleProjected: simpleBooked - safeCpm,
        };
      });
      break;
    case "CTR":
      updatedData = groupedData.map(item => {
        const { clicks, impressions, simpleBooked, totalClicks, totalImpressions } = item;

        const safeCtr = impressions !== 0 ? (Number(clicks) * 100) / Number(impressions) : 0;
        const safeTotalCtr =
          totalImpressions !== 0 ? (Number(totalClicks) * 100) / Number(totalImpressions) : 0;

        return {
          ...item,
          simpleCleared: safeTotalCtr,
          simpleProjected: simpleBooked - safeCtr,
        };
      });
      break;
    case "KPI Volume":
      updatedData = groupedData;
      break;
    default:
      updatedData = groupedData;
  }
  return updatedData;
};
